import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEnduserTimelineItems, type TimelineItem, type TimelineItemModel } from "@tellescope/tanstack-query";
import {
  applyMongoFilter,
  compileFreeformFilterBlock,
  type FreeformFieldOption,
  type FreeformFilterBlock,
  type MongoFilter,
} from "@tellescope/utils";
import { BlockProps, type TimelineFeature } from "../types";
import { useBlock } from ".";

/**
 * The user-selectable timeline item type values. `form_response_group` is what the timeline hook
 * emits for grouped form responses (surfaced in the filter UI as "Form Responses").
 */
export type TimelineItemFilterType = TimelineItem["_type"];

export type IsTimelineFeatureEnabled = (feature: TimelineFeature) => boolean;

const TIMELINE_ITEM_FEATURE: Partial<Record<TimelineItem["_type"], TimelineFeature>> = {
  sms_message: "messaging",
  email: "messaging",
  chat_room: "messaging",
  phone_call: "call",
  form_response_group: "forms",
};

const TIMELINE_ITEM_MODEL_ENTRIES: ReadonlyArray<[TimelineItem["_type"], TimelineItemModel]> = [
  ["phone_call", "phone_calls"],
  ["form_response_group", "form_responses"],
  ["sms_message", "sms_messages"],
  ["chat_room", "chat_rooms"],
  ["email", "emails"],
  ["note", "notes"],
];

export interface FilterTimelineItemsOptions {
  /**
   * The compiled Mongo filter derived from the block's freeform "Type" filter, or undefined when
   * no complete condition is set (in which case only feature gating applies). Applied per item
   * against a synthesized `{ type: item._type }` record.
   */
  compiledFilter?: MongoFilter;
  isFeatureEnabled?: IsTimelineFeatureEnabled;
}

/**
 * Applies timeline visibility rules to a list of items:
 *
 * 1. Org-feature gating (independent of the user filter): an item is dropped when its `_type` maps
 *    to a feature that `isFeatureEnabled` reports as disabled. Ungated types (e.g. `note`) always
 *    pass. When no `isFeatureEnabled` getter is supplied, no feature gating is applied.
 * 2. User type filter: `compiledFilter` (from the freeform "Type" filter) is evaluated against a
 *    synthesized `{ type: item._type }` record. When undefined (no complete condition), every
 *    feature-permitted item is kept.
 *
 * Both rules depend on `_type` alone. {@link resolveEnabledTimelineModels} relies on that to decide
 * a whole source's visibility from a synthesized `{ _type }` probe, so a new rule here that reads
 * any other field must not assume it is populated — otherwise the probe silently evaluates that rule
 * against a missing value and switches channels off incorrectly.
 *
 * Pure and hook-free so it can be unit-tested in isolation.
 */
export const filterTimelineItems = <T extends { _type: TimelineItem["_type"] }>(
  items: readonly T[],
  { compiledFilter, isFeatureEnabled }: FilterTimelineItemsOptions
): T[] => {
  return items.filter((item) => {
    const feature = TIMELINE_ITEM_FEATURE[item._type];
    if (feature && isFeatureEnabled && !isFeatureEnabled(feature)) {
      return false;
    }

    if (!compiledFilter) {
      return true;
    }

    return applyMongoFilter({ type: item._type }, compiledFilter);
  });
};

/**
 * Resolves which timeline source models are worth querying, by running the same visibility rules
 * {@link filterTimelineItems} applies per item against one representative item of each `_type`.
 *
 * Channels the user filtered out (or whose org feature is disabled) come back `false` so the caller
 * can switch those queries off entirely. Filtering only after fetching is not enough: the timeline
 * hook derives a shared pagination watermark from every source that still has pages, so a
 * filtered-out channel with a busy history would hold the watermark above the items the user asked
 * for, and `fetchNextPage` would keep spending pages on that channel — leaving a narrow filter (say,
 * notes only) showing nothing at all.
 *
 * The probe carries only `_type`, which is all {@link filterTimelineItems} reads; see the coupling
 * note there before adding a rule that inspects other fields.
 *
 * Pure and hook-free so it can be unit-tested in isolation.
 */
export const resolveEnabledTimelineModels = ({
  compiledFilter,
  isFeatureEnabled,
}: FilterTimelineItemsOptions): Record<TimelineItemModel, boolean> =>
  TIMELINE_ITEM_MODEL_ENTRIES.reduce<Record<TimelineItemModel, boolean>>(
    (acc, [type, model]) => ({
      ...acc,
      [model]: filterTimelineItems([{ _type: type }], { compiledFilter, isFeatureEnabled }).length > 0,
    }),
    {
      phone_calls: false,
      form_responses: false,
      sms_messages: false,
      chat_rooms: false,
      emails: false,
      notes: false,
    }
  );

export interface UseTimelineBlockArgs extends BlockProps {
  locale?: string;
  onOutOfCredits?: () => void;
}

export const useTimelineBlock = ({
  enduserId,
  info,
  editMode: providedEditMode,
  setEditMode: providedSetEditMode,
  viewId,
  blockId,
  blockIndex,
  blockLayout,
  isFeatureEnabled,
  locale,
  onOutOfCredits,
}: UseTimelineBlockArgs) => {
  const { t } = useTranslation("contacts");

  const block = useBlock({
    type: "timeline.timeline",
    info,
    editMode: providedEditMode,
    setEditMode: providedSetEditMode,
    enduserId,
    viewId,
    blockId,
    blockIndex,
    blockLayout,
  });

  const filterBlock = useMemo<FreeformFilterBlock>(
    () =>
      block.parsedInfo?.filter ?? {
        id: blockId ?? `${viewId}-${blockIndex}`,
        logic: "and",
        filters: [],
      },
    [block.parsedInfo?.filter, blockId, viewId, blockIndex]
  );

  const combineSMS = block.parsedInfo?.combineSMS ?? false;

  const filterFieldOptions = useMemo<FreeformFieldOption[]>(
    () => [
      {
        value: "type",
        label: t("Type"),
        valueType: "tags",
        operations: ["equals", "does_not_equal"],
      },
    ],
    [t]
  );

  const onFilterChange = useCallback(
    (nextBlock: FreeformFilterBlock) => {
      block.updateInfo({ ...block.parsedInfo, id: block.parsedInfo?.id ?? crypto.randomUUID(), filter: nextBlock });
    },
    [block]
  );

  const setCombineSMS = useCallback(
    (next: boolean) => {
      block.updateInfo({ ...block.parsedInfo, id: block.parsedInfo?.id ?? crypto.randomUUID(), combineSMS: next });
    },
    [block]
  );

  // Each item's `_type` is evaluated individually as a scalar `type` field, so array needles use
  // per-item semantics (equals matches any selected type; does-not-equal excludes them).
  const compiledFilter = useMemo(
    () => compileFreeformFilterBlock(filterBlock, { evalArraysByItem: true }),
    [filterBlock]
  );

  const enabledModels = useMemo(
    () => resolveEnabledTimelineModels({ compiledFilter, isFeatureEnabled }),
    [compiledFilter, isFeatureEnabled]
  );

  const { data, isLoading, fetchNextPage } = useEnduserTimelineItems({
    enduserId,
    combineSMSEntries: combineSMS,
    locale,
    onOutOfCredits,
    queryOptions: {
      phone_calls: { enabled: enabledModels.phone_calls },
      form_responses: { enabled: enabledModels.form_responses },
      sms_messages: { enabled: enabledModels.sms_messages },
      chat_rooms: { enabled: enabledModels.chat_rooms },
      emails: { enabled: enabledModels.emails },
      notes: { enabled: enabledModels.notes },
    },
  });

  const visibleItems = useMemo(
    () => filterTimelineItems<TimelineItem>(data ?? [], { compiledFilter, isFeatureEnabled }),
    [data, compiledFilter, isFeatureEnabled]
  );

  return {
    ...block,
    t,
    enduserId,
    filterBlock,
    filterFieldOptions,
    onFilterChange,
    combineSMS,
    setCombineSMS,
    items: visibleItems,
    isLoading,
    fetchNextPage,
  };
};
